import { splitFilters } from './fieldBindings';

/**
 * The part of a catalogue data point that decides the field name it is offered under.
 *
 * Structural on purpose, so both the `DataPoint` the catalogue holds and the richer shape the
 * editor's palette builds satisfy it without either having to know about the other.
 */
export interface DataPointName {
	label: string;
	/** A LiquidJS expression against the render root, filters and all. */
	path: string;
	/**
	 * The name to offer this point under, when it must NOT be derived from the label.
	 *
	 * A per-scheme point's label IS the scheme's mutable name, so a derived name changed on
	 * rename and orphaned the placed field. Named from the scheme id instead — `schemeFieldName`.
	 */
	fieldName?: string;
}

/** One path segment as snake_case: `firstName` → `first_name`. */
const snakeSegment = (part: string): string =>
	part
		// Two passes, or a run of capitals never splits: `isUSPerson` has to break before the P
		// as well as after the s, giving `is_us_person`.
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
		.replace(/([a-z0-9])([A-Z])/g, '$1_$2')
		.toLowerCase()
		.replace(/[^a-z0-9_]+/g, '_')
		.replace(/_+/g, '_')
		.replace(/^_+|_+$/g, '');

/**
 * A label as a name segment: "Total units held (all holdings)" → `total_units_held_all_holdings`.
 *
 * Exported so a scoped field name — which prefixes a segment the catalogue path doesn't carry,
 * e.g. the security name — can reuse the same grammar-safe sanitiser rather than growing a second,
 * subtly different one that would drift from what this module derives.
 */
export const labelSegment = (label: string): string =>
	label
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '');

const startsWithDigit = (segment: string): boolean => /^[0-9]/.test(segment);

/**
 * Prefixes a leading digit so a lone segment survives the token grammar, which rejects a whole
 * name when any segment starts with a digit. Exported so a scoped field name's security-name
 * segment gets the same guard `fieldNameFromPath` applies to a path segment, rather than a
 * second, unguarded copy.
 */
export const digitSafeSegment = (segment: string): string =>
	startsWithDigit(segment) ? `f_${segment}` : segment;

/**
 * A field name derived from a catalogue data point's Liquid path.
 *
 * `data.investor.firstName` → `investor.first_name`. The DOTS are kept: they are the only thing
 * that shows which object a value came from, and an analyst reading `{{ investor.last_name }}`
 * in a deed can see the hierarchy where `{{ investor_last_name }}` gives no way to tell which
 * underscore was the boundary. Each segment is still snake_case, so the name satisfies the token
 * grammar — the same name is written into the document as `{{ … }}` when it is read back as text.
 *
 * The `data.` root is dropped, because every catalogue path has it and a name beginning `data.`
 * would read as a direct Liquid expression rather than a field to link.
 *
 * Shared rather than owned by the editor because the IMPORT binds by this name too: if the two
 * derivations drifted, the importer would store a link under a name the palette never offers and
 * the field would read as bound while resolving to nothing.
 */
export const fieldNameFromPath = (path: string): string => {
	const segments = path
		.replace(/^data\./, '')
		.split(/[.[\]]+/)
		.map(snakeSegment)
		.filter((part) => part !== '');
	// A segment cannot START with a digit — the token grammar rejects the whole name if any does.
	// An array index is folded into the collection it indexes (`holdings[0]` → `holdings_0`)
	// rather than left as a level of its own, which is not a level an analyst thinks in anyway.
	const levels: string[] = [];
	for (const segment of segments) {
		const last = levels[levels.length - 1];
		if (startsWithDigit(segment) && last !== undefined) {
			levels[levels.length - 1] = `${last}_${segment}`;
			continue;
		}
		levels.push(digitSafeSegment(segment));
	}
	const name = levels.join('.');
	return name === '' ? 'field' : name;
};

/**
 * The field name and binding a catalogue data point should be offered under.
 *
 * The filter is split off the path before the name is derived. Deriving it from the whole
 * expression folded the FORMAT into the name — `data.investor.birthDate | date: "%d %b %Y"`
 * became `investor.birth_date_date_d_b_y`, which is what the author saw on the chip — and made
 * two aggregates over one list collide on the head of their path.
 *
 * A single filter rides on the field NODE, where it is per-occurrence and the author can change
 * the format of one date without touching the others. A chain of them cannot (the token grammar
 * takes one), so it stays inside the binding and the label distinguishes the name.
 */
export const fieldForDataPoint = (
	point: DataPointName
): { name: string; binding: string; filter?: string } => {
	const { expression, filters } = splitFilters(point.path);
	// Wins outright: supplied precisely because nothing derivable here is stable enough to key
	// a saved document on.
	if (point.fieldName !== undefined) {
		return { name: point.fieldName, binding: `{{ ${point.path} }}` };
	}
	const base = fieldNameFromPath(expression);
	if (filters.length === 1) {
		return { name: base, binding: `{{ ${expression} }}`, filter: filters[0] };
	}
	if (filters.length === 0) {
		return { name: base, binding: `{{ ${expression} }}` };
	}
	// `digitSafeSegment` because the label is author-written: "2026 Tier" would otherwise mint a
	// segment the token grammar rejects, rendering blank while the palette showed a sample.
	return {
		name: `${base}.${digitSafeSegment(labelSegment(point.label))}`,
		binding: `{{ ${point.path} }}`,
	};
};

/**
 * The bindings the catalogue can supply for a set of field names, keyed by name.
 *
 * What makes an imported .docx usable without a second pass: a token the author already wrote
 * as a catalogue name — `{{ investor.first_name }}` — is a data point they have identified, so
 * it is linked where the document is stored rather than left for a save to fill in. A name the
 * catalogue cannot account for is absent, which is the one thing the analyst has to act on.
 *
 * First point wins where two derive the same name, matching the order the palette offers them
 * in; the palette suffixes the loser, and a suffixed name is not one an import can guess.
 */
export const catalogueFieldBindings = (
	names: ReadonlySet<string>,
	catalogue: readonly DataPointName[]
): Record<string, string> => {
	const bindings: Record<string, string> = {};
	for (const point of catalogue) {
		const { name, binding } = fieldForDataPoint(point);
		if (names.has(name) && bindings[name] === undefined) {
			bindings[name] = binding;
		}
	}
	return bindings;
};
